import * as Bowser from 'bowser';

export const getBowserInfos = () => {
  const browser = Bowser.getParser(window.navigator.userAgent);
  const browserResult = browser.getResult();

  // console.log({
  //   deviceModel: `${browserResult.platform.model}`,
  //   deviceType: `${browserResult.platform.model}`,
  //   osVersion: `${browserResult.os.version}`,
  // });

  return {
    deviceModel: `${browserResult.platform.model || ''}`,
    deviceType: `${browserResult.platform.model || ''}`,
    osVersion: `${browserResult.os.version || ''}`,
  };
};

/**
 * 操作系统检测配置
 * 定义了 UA 匹配规则和对应的操作系统名称
 */
interface OsDetectionRule {
  pattern: RegExp;
  name: string;
  versionExtractor?: (ua: string, appVersion: string) => string;
}

/**
 * 标准化操作系统名称映射表
 * 将 Bowser 或 UA 检测的操作系统名称转换为统一的小写格式
 */
const OS_NAME_MAP: Record<string, string> = {
  // iOS 系列
  ios: 'ios',
  ipad: 'ios',
  ipados: 'ios',

  // Android 系列
  android: 'android',

  // HarmonyOS 系列
  harmonyos: 'harmony',

  // Windows 系列
  windows: 'windows',
  'windows phone': 'windows',

  // macOS 系列
  macos: 'macos',
  'mac os': 'macos',

  // ChromeOS 系列
  chromeos: 'chromeos',
  'chrome os': 'chromeos',

  // Linux 发行版
  ubuntu: 'ubuntu',
  fedora: 'fedora',
  debian: 'debian',
  centos: 'centos',
  linux: 'linux',

  // Unix 系列
  freebsd: 'freebsd',
  openbsd: 'openbsd',
  sunos: 'sunos',
  unix: 'unix',
};

/**
 * 操作系统检测规则列表
 * 按优先级排序,优先级高的规则会先被匹配
 */
const OS_DETECTION_RULES: OsDetectionRule[] = [
  // 移动设备
  {
    pattern: /iphone|ipad|ipod|ios/i,
    name: 'iOS',
    versionExtractor: (ua, appVersion) => {
      const match = ua.match(/OS\s+([\d._]+)/i) || ua.match(/Version\/([\d.]+)/i) || appVersion.match(/OS\s+([\d._]+)/i);
      return match && match[1] ? match[1].replace(/_/g, '.') : '';
    }
  },
  {
    pattern: /android/i,
    name: 'Android',
    versionExtractor: (ua) => {
      const match = ua.match(/Android\s+([\d.]+)/i);
      return match && match[1] ? match[1] : '';
    }
  },
  {
    pattern: /openharmony|harmony/i,
    name: 'HarmonyOS',
    versionExtractor: (ua) => {
      const match = ua.match(/HarmonyOS[/\s]([\d.]+)/i);
      return match && match[1] ? match[1] : '';
    }
  },

  // 桌面系统
  {
    pattern: /windows|win32|win64/i,
    name: 'Windows',
    versionExtractor: (ua) => {
      if (/Windows NT 10.0/.test(ua)) return '10';
      if (/Windows NT 6.3/.test(ua)) return '8.1';
      if (/Windows NT 6.2/.test(ua)) return '8';
      if (/Windows NT 6.1/.test(ua)) return '7';
      if (/Windows NT 6.0/.test(ua)) return 'Vista';
      if (/Windows NT 5.1/.test(ua)) return 'XP';
      const match = ua.match(/Windows NT\s+([\d.]+)/i);
      return match && match[1] ? match[1] : '';
    }
  },
  {
    pattern: /macintosh|mac os x|macos/i,
    name: 'macOS',
    versionExtractor: (ua, appVersion) => {
      let match = ua.match(/Mac OS X\s+([0-9_.]+)/i);
      if (!match) match = appVersion.match(/Mac OS X\s+([0-9_.]+)/i);
      return match && match[1] ? match[1].replace(/_/g, '.') : '';
    }
  },

  // Linux 系列 (按优先级排序)
  {
    pattern: /cros/i,
    name: 'ChromeOS',
    versionExtractor: (ua) => {
      const match = ua.match(/CrOS\s+[a-z_]*\s+([\d.]+)/i);
      return match && match[1] ? match[1] : '';
    }
  },
  {
    pattern: /ubuntu/i,
    name: 'Ubuntu'
  },
  {
    pattern: /fedora/i,
    name: 'Fedora'
  },
  {
    pattern: /debian/i,
    name: 'Debian'
  },
  {
    pattern: /centos/i,
    name: 'CentOS'
  },
  {
    pattern: /linux|x11/i,
    name: 'Linux'
  },

  // Unix 系列
  {
    pattern: /freebsd/i,
    name: 'FreeBSD'
  },
  {
    pattern: /openbsd/i,
    name: 'OpenBSD'
  },
  {
    pattern: /sunos/i,
    name: 'SunOS'
  },
  {
    pattern: /unix/i,
    name: 'Unix'
  }
];

/**
 * 标准化操作系统名称
 * 将 Bowser 或 UA 检测的操作系统名称转换为统一的小写格式
 */
function normalizeOsName(osName: string): string {
  if (!osName) return '';
  const normalized = OS_NAME_MAP[osName.toLowerCase()];
  return normalized || '';
}

/**
 * 从 User Agent 检测操作系统
 * 使用规则列表进行匹配,返回操作系统名称和版本
 */
function detectOsFromUa(userAgent: string, appVersion: string = ''): { name: string; version: string; } {
  for (const rule of OS_DETECTION_RULES) {
    if (rule.pattern.test(userAgent)) {
      const version = rule.versionExtractor
        ? rule.versionExtractor(userAgent, appVersion)
        : '';

      return {
        name: rule.name,
        version
      };
    }
  }

  return { name: '', version: '' };
}

/**
 * 使用 platform API 作为最后的降级方案检测操作系统
 */
function detectOsFromPlatform(platform: string): string {
  if (/Win/.test(platform)) {
    return 'windows';
  } else if (/Mac/.test(platform) && !/iPhone|iPad|iPod/.test(platform)) {
    return 'macos';
  } else if (/(iPhone|iPad|iPod)/.test(platform)) {
    return 'ios';
  } else if (/Android/.test(platform)) {
    return 'android';
  } else if (/Linux/.test(platform)) {
    return 'linux';
  }
  return '';
}

/**
 * 规范化返回结果
 * 处理 unknown 值和版本号清理
 */
function normalizeResult(os: string, osVersion: string): { os: string; osVersion: string; } {
  // 如果操作系统未检测到,设置为 unknown
  if (!os) {
    os = 'unknown';
  }

  // 如果版本未检测到,设置为 unknown
  if (!osVersion) {
    osVersion = 'unknown';
  }

  // 如果版本为 unknown 但操作系统已知,清空版本
  if (osVersion === 'unknown' && os !== 'unknown') {
    osVersion = '';
  }

  return { os, osVersion };
}

/**
 * 获取设备操作系统信息
 * 优先级: Bowser 库 > UA 规则检测 > platform API > unknown
 * @returns {Object} 包含 os、osVersion 和 language 的对象
 */
export const getBowserDeviceInfo = () => {
  const userAgent = navigator.userAgent || '';
  const appVersion = navigator.appVersion || '';
  const platform = navigator.platform || '';
  const language = navigator.language || '';

  let os = '';
  let osVersion = '';

  try {
    // 第一步: 尝试使用 Bowser 库
    const browser = Bowser.getParser(userAgent);
    const browserResult = browser.getResult();

    if (browserResult.os?.name) {
      os = normalizeOsName(browserResult.os.name);
      osVersion = browserResult.os.version || '';

      // 如果 Bowser 检测成功,直接返回
      if (os) {
        const result = normalizeResult(os, osVersion);
        return { ...result, language };
      }
    }
  } catch (e) {
    console.warn('Bowser detection failed:', e);
  }

  // 第二步: 使用 UA 规则检测
  const uaDetection = detectOsFromUa(userAgent, appVersion);
  if (uaDetection.name) {
    os = normalizeOsName(uaDetection.name);
    osVersion = uaDetection.version;

    if (os) {
      const result = normalizeResult(os, osVersion);
      return { ...result, language };
    }
  }

  // 第三步: 使用 platform API 作为最后的降级方案
  os = detectOsFromPlatform(platform);

  // 规范化结果
  const result = normalizeResult(os, osVersion);
  return { ...result, language };
};
